import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Μπορώ να πάω θάλασσα μετά από τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι αμέσως. Πρέπει να περιμένεις τουλάχιστον δύο με τρεις εβδομάδες, μέχρι να ολοκληρωθεί η επιφανειακή επούλωση και να μην υπάρχει πια ανοιχτή πληγή. Το φρέσκο τατουάζ στη θάλασσα κινδυνεύει από μόλυνση και ξεθώριασμα." } },
    { "@type": "Question", name: "Πόσο πρέπει να περιμένω πριν κολυμπήσω με νέο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ο γενικός κανόνας είναι δύο με τρεις εβδομάδες, αλλά ο ακριβής χρόνος εξαρτάται από το μέγεθος, το σημείο και τον οργανισμό σου. Περίμενε μέχρι να τελειώσει εντελώς το ξεφλούδισμα και το δέρμα να φαίνεται λείο και φυσιολογικό." } },
    { "@type": "Question", name: "Είναι η πισίνα πιο ασφαλής από τη θάλασσα για φρέσκο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Η πισίνα περιέχει χλώριο και χημικά που ερεθίζουν το τραυματισμένο δέρμα, ενώ η θάλασσα περιέχει αλάτι και βακτήρια. Και οι δύο πρέπει να αποφεύγονται μέχρι την πλήρη επιφανειακή επούλωση." } },
    { "@type": "Question", name: "Μπορώ να κάνω ντους με φρέσκο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ναι. Το γρήγορο ντους με καθαρό, τρεχούμενο νερό είναι απαραίτητο για την καθαριότητα. Απόφυγε όμως το παρατεταμένο μούλιασμα στην μπανιέρα, το πολύ ζεστό νερό και την πίεση του νερού απευθείας πάνω στην περιοχή." } },
    { "@type": "Question", name: "Τι να κάνω αν βραχεί κατά λάθος το τατουάζ μου στη θάλασσα;", acceptedAnswer: { "@type": "Answer", text: "Βγες από το νερό, ξέπλυνε απαλά με καθαρό γλυκό νερό για να φύγει το αλάτι, στέγνωσε ταμποναριστά και βάλε λεπτή στρώση ενυδατικής. Παρακολούθησε την περιοχή και αν δεις σημάδια μόλυνσης, επικοινώνησε με τον tattoo artist σου ή με γιατρό." } },
    { "@type": "Question", name: "Μπορώ να βάλω αντηλιακό πάνω στο φρέσκο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι όσο είναι ανοιχτή πληγή. Το αντηλιακό μπαίνει μόνο μετά την πλήρη επούλωση. Όσο επουλώνεται, κράτα το τατουάζ καλυμμένο και μακριά από τον ήλιο." } },
    { "@type": "Question", name: "Βοηθάει το αλάτι της θάλασσας την επούλωση του τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι, αυτός είναι μύθος. Παρά τις αντισηπτικές ιδιότητες του αλατιού, το θαλασσινό νερό περιέχει βακτήρια και μικροοργανισμούς που αυξάνουν τον κίνδυνο μόλυνσης και ξεθωριάσματος του φρέσκου τατουάζ." } },
  ],
};

export default function SeaAfterTattooAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Μπορώ να πάω θάλασσα μετά από τατουάζ;",
      "description": "Πόσο πρέπει να περιμένεις πριν πας θάλασσα με φρέσκο τατουάζ, τι κινδύνους κρύβει η θάλασσα και η πισίνα, και πώς να απολαύσεις το καλοκαίρι χωρίς ρίσκο.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-06-15",
      "dateModified": "2026-06-15",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/sea-after-tattoo-athens",
      "inLanguage": "el"
    };
    inject('schema-sea-after-tattoo-faq', faqSchema);
    inject('schema-sea-after-tattoo-article', articleSchema);
    return () => {
      document.getElementById('schema-sea-after-tattoo-faq')?.remove();
      document.getElementById('schema-sea-after-tattoo-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Μπορώ να πάω θάλασσα μετά από τατουάζ; | 210 Tattoo Athens</title>
        <meta name="description" content="Πόσο πρέπει να περιμένεις πριν πας θάλασσα με φρέσκο τατουάζ, τι κινδύνους κρύβει η θάλασσα και η πισίνα, και πώς να απολαύσεις το καλοκαίρι χωρίς ρίσκο." />
        <link rel="canonical" href="https://210tattoo.gr/blog/sea-after-tattoo-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/sea-after-tattoo-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/sea-after-tattoo-athens" />
        <meta property="og:title" content="Μπορώ να πάω θάλασσα μετά από τατουάζ; | 210 Tattoo Athens" />
        <meta property="og:description" content="Πόσο πρέπει να περιμένεις πριν πας θάλασσα με φρέσκο τατουάζ και πώς να προστατεύσεις το νέο σου κομμάτι." />
        <meta property="og:url" content="https://210tattoo.gr/blog/sea-after-tattoo-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('sea-after-tattoo-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Μπορώ να πάω θάλασσα μετά από τατουάζ; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Τι πρέπει να ξέρεις πριν πας θάλασσα με φρέσκο τατουάζ." />
        <meta name="twitter:image" content={ogImage('sea-after-tattoo-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Τατουάζ &amp; Θάλασσα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Μπορώ να πάω θάλασσα</span>
              <br />
              <span className="text-white">μετά από τατουάζ;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">15 Ιουνίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Η σύντομη απάντηση είναι όχι, όχι αμέσως. Δες γιατί, πόσο πρέπει να περιμένεις και πώς να απολαύσεις το καλοκαίρι σου χωρίς να ρισκάρεις το νέο σου τατουάζ.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Why */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί η θάλασσα και το φρέσκο τατουάζ <span className="text-pink-500">δεν πάνε μαζί</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Όταν ο tattoo artist δουλεύει, η βελόνα δημιουργεί χιλιάδες μικροσκοπικές τρύπες στο δέρμα. Το νέο σου τατουάζ είναι ουσιαστικά μια ανοιχτή πληγή που χρειάζεται χρόνο για να κλείσει.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Όσο αυτή η πληγή είναι ανοιχτή, είναι ευάλωτη. Η θάλασσα, όσο καθαρή κι αν φαίνεται, είναι γεμάτη βακτήρια, μικροοργανισμούς και αλάτι. Το να βυθίσεις μια ανοιχτή πληγή σε αυτό το περιβάλλον ανοίγει την πόρτα σε προβλήματα.
            </p>
          </div>
        </section>

        {/* Risks */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Οι κίνδυνοι του να πας <span className="text-pink-500">θάλασσα με φρέσκο τατουάζ</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Κίνδυνος μόλυνσης', desc: 'Το θαλασσινό νερό περιέχει βακτήρια που μπορούν να εισέλθουν στην ανοιχτή πληγή και να καταστρέψουν μόνιμα κομμάτια του σχεδίου.' },
                { title: 'Ξεθώριασμα του χρώματος', desc: 'Το αλάτι τραβάει την υγρασία από το δέρμα και επηρεάζει το μελάνι που δεν έχει σταθεροποιηθεί ακόμα.' },
                { title: 'Πρόωρη απομάκρυνση των φλουδιών', desc: 'Το νερό μαλακώνει τις φλούδες πρόωρα, βγάζοντας μαζί και μελάνι, δημιουργώντας κενά.' },
                { title: 'Παράταση της επούλωσης', desc: 'Η έκθεση στη θάλασσα ερεθίζει την περιοχή και επιβραδύνει τη φυσική διαδικασία επούλωσης.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How long */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πόσο πρέπει να περιμένω <span className="text-pink-500">πριν πάω θάλασσα;</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ο γενικός κανόνας είναι να περιμένεις τουλάχιστον δύο με τρεις εβδομάδες, μέχρι να ολοκληρωθεί η επιφανειακή επούλωση. Ένα μικρό, απλό τατουάζ μπορεί να επουλωθεί πιο γρήγορα, ενώ ένα μεγάλο, γεμάτο κομμάτι χρειάζεται περισσότερο χρόνο.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Ο πιο σίγουρος τρόπος να ξέρεις πότε είσαι έτοιμος είναι να παρατηρήσεις το δέρμα σου. Όταν δεν είναι πια ευαίσθητο, έχει σταματήσει το ξεφλούδισμα και φαίνεται φυσιολογικό, μπορείς να σκέφτεσαι τη θάλασσα.
            </p>
          </div>
        </section>

        {/* Sea vs pool vs lake */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θάλασσα, πισίνα και λίμνη, <span className="text-pink-500">υπάρχει διαφορά;</span></h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: 'Θάλασσα', desc: 'Περιέχει αλάτι και φυσικά βακτήρια. Το αλάτι ξεθωριάζει το μελάνι, τα βακτήρια αυξάνουν τον κίνδυνο μόλυνσης.' },
                { title: 'Πισίνα', desc: 'Περιέχει χλώριο και χημικά που ερεθίζουν το τραυματισμένο δέρμα και επηρεάζουν αρνητικά την επούλωση.' },
                { title: 'Λίμνες & ποτάμια', desc: 'Ίσως τα πιο επικίνδυνα, λόγω στάσιμου ή φυσικού νερού με υψηλά επίπεδα βακτηρίων.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Shower & sun */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Τι γίνεται με <span className="text-pink-500">το ντους;</span></h2>
                <p className="text-gray-300 leading-relaxed">
                  Το γρήγορο ντους με καθαρό, τρεχούμενο νερό είναι όχι μόνο επιτρεπτό αλλά απαραίτητο. Η διαφορά με τη θάλασσα είναι το είδος και η διάρκεια της επαφής με το νερό. Απόφυγε τα παρατεταμένα λουτρά και το πολύ ζεστό νερό.
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Ο ήλιος, <span className="text-pink-500">ο δεύτερος εχθρός</span></h2>
                <p className="text-gray-300 leading-relaxed">
                  Η υπεριώδης ακτινοβολία μπορεί να ξεθωριάσει το χρώμα πριν καν σταθεροποιηθεί. Ακόμα και μετά την πλήρη επούλωση, πρέπει πάντα να προστατεύεις το τατουάζ με αντηλιακό υψηλού δείκτη.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Enjoy summer tips */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να απολαύσεις το καλοκαίρι <span className="text-pink-500">με φρέσκο τατουάζ</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Προγραμμάτισε το τατουάζ έξυπνα', desc: 'Κάνε το αρκετές εβδομάδες πριν φύγεις διακοπές, ή μετά, ώστε να μην μπεις στον πειρασμό να μπεις πρόωρα στη θάλασσα.' },
                { title: 'Πήγαινε παραλία αλλά μείνε στη σκιά', desc: 'Μπορείς να απολαύσεις την παρέα και την ατμόσφαιρα χωρίς να μπεις στο νερό.' },
                { title: 'Κάλυψε το τατουάζ από τον ήλιο', desc: 'Ένα ελαφρύ, καθαρό ρούχο πάνω από την περιοχή είναι μια καλή λύση. Μην βάζεις αντηλιακό σε μη επουλωμένο τατουάζ.' },
                { title: 'Συνέχισε τη φροντίδα σου', desc: 'Κράτα μαζί σου το ήπιο σαπούνι και την ενυδατική σου, ακόμα και στις διακοπές.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Accident */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να κάνεις αν βραχεί <span className="text-pink-500">κατά λάθος το τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Βγες από το νερό το συντομότερο δυνατό, ξέπλυνε απαλά την περιοχή με καθαρό, γλυκό νερό για να φύγει το αλάτι ή το χλώριο, στέγνωσε ταμποναριστά και βάλε μια λεπτή στρώση ενυδατικής. Αν παρατηρήσεις κοκκίνισμα, πρήξιμο ή σημάδια μόλυνσης, επικοινώνησε με τον tattoo artist σου ή με γιατρό.
            </p>
          </div>
        </section>

        {/* Myths */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μύθοι και αλήθειες <span className="text-pink-500">για τατουάζ και θάλασσα</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Μύθος: Το αλάτι βοηθάει την επούλωση', desc: 'Λάθος. Το θαλασσινό νερό περιέχει βακτήρια και κάνει περισσότερο κακό παρά καλό σε ένα φρέσκο τατουάζ.' },
                { title: 'Μύθος: Αν είναι μικρό τατουάζ, δεν πειράζει', desc: 'Το μέγεθος δεν αλλάζει το γεγονός ότι πρόκειται για ανοιχτή πληγή, ευάλωτη σε μόλυνση.' },
                { title: 'Μύθος: Το αδιάβροχο επίθεμα λύνει το πρόβλημα', desc: 'Προσφέρει κάποια προστασία για σύντομη επαφή, αλλά δεν αντικαθιστά την υπομονή.' },
                { title: 'Αλήθεια: Η υπομονή είναι το καλύτερο που μπορείς να κάνεις', desc: 'Λίγες εβδομάδες αναμονής είναι μικρή θυσία μπροστά σε ένα τατουάζ που θα κουβαλάς για πάντα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θες να προγραμματίσεις <span className="text-pink-500">σωστά το τατουάζ σου;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Η ομάδα του 210 Tattoo Athens θα σε καθοδηγήσει ώστε το τατουάζ σου να επουλωθεί τέλεια και να είσαι έτοιμος για τη θάλασσα τη σωστή στιγμή.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM στο 210 Tattoo Athens
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/summer-tattoo-care-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τατουάζ το Καλοκαίρι: Ήλιος, Θάλασσα και Φροντίδα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Όλα όσα πρέπει να ξέρεις για tattoo το καλοκαίρι.</p>
              </Link>
              <Link to="/blog/tattoo-healing-time-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Όλα τα στάδια επούλωσης και πώς να φροντίσεις σωστά το νέο σου τατουάζ.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
